/**
 * Кадрирование готового кадра под формат ролика 9:16 (задача Оли №323: «если загружен кадр не 9:16…
 * должна быть возможность кадрирования»). Рамка та же, что у публикаций (`CropFrame`), пропорция
 * одна — 9:16; результат отдельной бесплатной генерацией `upload`, оригинал остаётся.
 *
 * Куда встанет обрезанный кадр, решает вызывающий: `projectId` — исходный кадр проекта «из кадра»
 * (сервер двигает и ссылку проекта, и стилл), `shotId` — обычный кадр (стилл).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { Dialog } from "@/components/trends/Dialog";
import { Btn, ErrorLine, Hint } from "@/components/project/ui";
import { mediaUrl } from "@/lib/media";
import { CropFrame } from "@/components/posts/CropFrame";
import type { Crop } from "@/lib/postPlan";
import { errorText, postJson, qk } from "@/lib/project";
import { videoKeys } from "@/lib/video";

export function CropDialog({
  open,
  onClose,
  pid,
  variantId,
  assetPath,
  projectId,
  shotId,
}: {
  open: boolean;
  onClose: () => void;
  /** проект, чью карточку перечитываем после обрезки */
  pid: number;
  variantId: number;
  assetPath: string;
  projectId: number | null;
  shotId: number | null;
}) {
  const client = useQueryClient();
  const [crop, setCrop] = useState<Crop | null>(null);

  const cut = useMutation({
    mutationFn: () =>
      // рамку не двигали — долей не шлём: сервер возьмёт 9:16 по центру той же арифметикой,
      // которой до сих пор резал кадр перед отправкой в видео-модель
      postJson<{ variant_id: number }>(`/api/results/variants/${variantId}/crop`, {
        ...(crop ?? {}),
        ...(projectId !== null ? { project_id: projectId } : {}),
        ...(projectId === null && shotId !== null ? { shot_id: shotId } : {}),
      }),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: qk.project(pid) }),
        client.invalidateQueries({ queryKey: videoKeys.poll(pid) }),
      ]);
      setCrop(null);
      onClose();
    },
  });

  return (
    <Dialog open={open} title="Кадрировать под 9:16" onClose={onClose}>
      <div className="grid gap-3 p-3">
        <CropFrame
          src={mediaUrl(assetPath)}
          width={null}
          height={null}
          aspect="9:16"
          crop={crop}
          onCommit={setCrop}
        />
        <Hint>
          Обрезанный кадр станет картинкой этого ракурса, а оригинал останется в «Результатах». Бесплатно.
        </Hint>
        <div className="flex flex-wrap items-center gap-2">
          <Btn tone="primary" disabled={cut.isPending} onClick={() => cut.mutate()}>
            {cut.isPending ? "режу…" : "Обрезать"}
          </Btn>
          <Btn onClick={onClose}>Отмена</Btn>
        </div>
        <ErrorLine text={cut.isError ? errorText(cut.error) : ""} />
      </div>
    </Dialog>
  );
}
